/** Marketing parenting plan tool: schedule catalog + shared types. */

export const PARENTING_PLAN_SCHEDULE_SLUGS = [
  '2-2-3',
  '2-2-5-5',
  'week-on-week-off',
  '3-4-4-3',
  '50-50',
  '60-40',
  'long-distance',
  'newborn',
  'first-time',
  'holidays-only',
  'summer',
  'every-extended-weekend',
] as const;

export type ParentingPlanScheduleSlug = (typeof PARENTING_PLAN_SCHEDULE_SLUGS)[number];

export type ParentingPlanAnswers = {
  children?: {
    items?: Array<{ name?: string; birthYear?: string }>;
  };
  custody?: {
    scheduleSlug?: string;
    startDate?: string;
    notes?: string;
  };
  holidays?: { notes?: string };
  communication?: {
    channel?: string;
    responseTime?: string;
    notes?: string;
  };
  expenses?: { split?: string; notes?: string };
  decisions?: { notes?: string };
};

export type SchedulePageMeta = {
  slug: ParentingPlanScheduleSlug;
  title: string;
  shortTitle: string;
  description: string;
  /** 14-day visualization: which parent has overnight (A = first parent) */
  pattern14: Array<'A' | 'B'>;
};

/** 14-night pattern approximations for marketing visuals (not legal definitions). */
export const SCHEDULE_PAGE_META: Record<ParentingPlanScheduleSlug, SchedulePageMeta> = {
  '2-2-3': {
    slug: '2-2-3',
    shortTitle: '2-2-3',
    title: '2-2-3 custody schedule',
    description:
      'A two-week rotation that alternates short blocks with a long weekend swap, common when parents live close and young children benefit from frequent contact.',
    pattern14: [
      'A',
      'A',
      'B',
      'B',
      'A',
      'A',
      'A',
      'B',
      'B',
      'A',
      'A',
      'B',
      'B',
      'B',
    ],
  },
  '2-2-5-5': {
    slug: '2-2-5-5',
    shortTitle: '2-2-5-5',
    title: '2-2-5-5 custody schedule',
    description:
      'Two days with one parent, two with the other, then a five-day block each. It balances weekday routines with longer stretches.',
    pattern14: [
      'A',
      'A',
      'B',
      'B',
      'A',
      'A',
      'A',
      'A',
      'A',
      'B',
      'B',
      'B',
      'B',
      'B',
    ],
  },
  'week-on-week-off': {
    slug: 'week-on-week-off',
    shortTitle: 'Week on / week off',
    title: 'Week-on, week-off schedule',
    description:
      'Alternating full weeks with each parent, simple to explain and often used when kids are school-aged and transitions are weekly.',
    pattern14: [
      ...Array(7).fill('A'),
      ...Array(7).fill('B'),
    ] as Array<'A' | 'B'>,
  },
  '3-4-4-3': {
    slug: '3-4-4-3',
    shortTitle: '3-4-4-3',
    title: '3-4-4-3 custody schedule',
    description:
      'A two-week pattern mixing 3- and 4-day stretches, useful when you want predictable longer blocks without going full weeks.',
    pattern14: [
      'A',
      'A',
      'A',
      'B',
      'B',
      'B',
      'B',
      'A',
      'A',
      'A',
      'A',
      'B',
      'B',
      'B',
    ],
  },
  '50-50': {
    slug: '50-50',
    shortTitle: '50/50',
    title: '50/50 parenting time',
    description:
      'Equal parenting time can take many calendar shapes; week-on/week-off is one of the clearest 50/50 approaches for school schedules.',
    pattern14: [...Array(7).fill('A'), ...Array(7).fill('B')] as Array<'A' | 'B'>,
  },
  '60-40': {
    slug: '60-40',
    shortTitle: '60/40',
    title: '60/40 parenting time',
    description:
      'Unequal splits are common when work schedules, distance, or school logistics make an even split difficult. Document the exact pattern you intend.',
    pattern14: [
      'A',
      'A',
      'A',
      'A',
      'B',
      'B',
      'B',
      'A',
      'A',
      'A',
      'A',
      'B',
      'B',
      'B',
    ],
  },
  'long-distance': {
    slug: 'long-distance',
    shortTitle: 'Long distance',
    title: 'Long-distance parenting plan',
    description:
      'When parents live far apart, plans usually emphasize longer visits, holidays, and summer, plus clear travel and communication rules.',
    pattern14: [
      'B',
      'B',
      'B',
      'B',
      'B',
      'A',
      'A',
      'B',
      'B',
      'B',
      'B',
      'B',
      'A',
      'A',
    ],
  },
  newborn: {
    slug: 'newborn',
    shortTitle: 'Newborn',
    title: 'Parenting plan with a newborn',
    description:
      'Infant plans often include shorter frequent visits, gradual increases, and breastfeeding considerations. Capture what you agree on in writing.',
    pattern14: [
      'A',
      'A',
      'B',
      'B',
      'A',
      'A',
      'A',
      'B',
      'B',
      'A',
      'A',
      'B',
      'B',
      'B',
    ],
  },
  'first-time': {
    slug: 'first-time',
    shortTitle: 'First plan',
    title: 'First parenting plan',
    description:
      'If this is your first written plan, focus on holidays, communication, and how you will handle changes. Courts favor specificity and good faith.',
    pattern14: [...Array(7).fill('A'), ...Array(7).fill('B')] as Array<'A' | 'B'>,
  },
  'holidays-only': {
    slug: 'holidays-only',
    shortTitle: 'Holidays',
    title: 'Holiday & break schedule',
    description:
      'Some families keep a simple base schedule and spell out holidays, school breaks, and summers in detail. Reduce conflict with clear alternation rules.',
    pattern14: [...Array(7).fill('A'), ...Array(7).fill('B')] as Array<'A' | 'B'>,
  },
  summer: {
    slug: 'summer',
    shortTitle: 'Summer',
    title: 'Summer parenting time',
    description:
      'Summer blocks, camps, and travel windows are a common source of disputes. Define pick-up/drop-off, notice periods, and passports early.',
    pattern14: [...Array(7).fill('A'), ...Array(7).fill('B')] as Array<'A' | 'B'>,
  },
  'every-extended-weekend': {
    slug: 'every-extended-weekend',
    shortTitle: 'Extended weekends',
    title: 'Every extended weekend',
    description:
      'One parent may take most weekends (or extended weekends) while the other has weekdays, useful for work travel or school stability.',
    pattern14: [
      'B',
      'B',
      'B',
      'B',
      'B',
      'A',
      'A',
      'B',
      'B',
      'B',
      'B',
      'B',
      'A',
      'A',
    ],
  },
};

export function isScheduleSlug(s: string): s is ParentingPlanScheduleSlug {
  return (PARENTING_PLAN_SCHEDULE_SLUGS as readonly string[]).includes(s);
}
